import { useEffect, useRef } from "react";
import Hls from "hls.js";

/**
 * HlsVideoPlayer
 * - Renders an HLS (.m3u8) live stream via hls.js where supported, with a
 *   native Safari/iOS fallback (browsers with built-in HLS).
 * - Designed for the Betway live broadcast pipeline where the manifest
 *   URL is captured server-side via Playwright (see `betway_stream_extractor.py`).
 *
 * Notes:
 * - HLS tokens rotate every ~5-10 min upstream. The backend caches the URL
 *   for 6 min and the consumer should re-call `/api/streams/{fid}` if the
 *   `<video>` element fires `error`.
 * - DRM-protected segments will fail to decrypt; this is by design — the
 *   consumer of this component should fall back to aiscore on `onError`.
 */
export const HlsVideoPlayer = ({ src, onError, onReady, autoPlay = true, muted = true, className = "" }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    let cancelled = false;

    // Safari / iOS — native HLS support
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      const onLoaded = () => onReady && onReady();
      const onErr = () => onError && onError();
      video.addEventListener("loadedmetadata", onLoaded);
      video.addEventListener("error", onErr);
      return () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        video.removeEventListener("error", onErr);
        try { video.pause(); video.removeAttribute("src"); video.load(); } catch {}
      };
    }

    // Chromium / Firefox — hls.js
    if (Hls.isSupported()) {
      const hls = new Hls({
        liveBackBufferLength: 30,
        maxBufferLength: 30,
        lowLatencyMode: true,
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!cancelled) onReady && onReady();
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data?.fatal && !cancelled) {
          onError && onError(data);
        }
      });
      return () => {
        cancelled = true;
        try { hls.destroy(); } catch {}
        hlsRef.current = null;
      };
    }

    // Fallback — browser doesn't support HLS at all
    onError && onError({ fatal: true, type: "unsupported" });
  }, [src, onError, onReady]);

  return (
    <video
      ref={videoRef}
      data-testid="hls-video-player"
      className={className}
      autoPlay={autoPlay}
      muted={muted}
      playsInline
      controls
    />
  );
};

export default HlsVideoPlayer;
